import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import {
  Category,
  StoreCategorySchema,
} from '../../../shared/models/catalog/category.model';
import { CategoryService } from '../../../shared/services/catalog/category.service';
import { ContingentService } from '../../../shared/services/catalog/contingent.service';
import { applyZodErrors } from '../../../shared/util/zod-form-errors';

@Component({
  selector: 'app-categories-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './categories-page.component.html',
  styleUrl: './categories-page.component.scss',
})
export class CategoriesPageComponent implements OnInit {
  categoryService = inject(CategoryService);
  contingentService = inject(ContingentService);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translate = inject(TranslateService);
  #fb = inject(FormBuilder);

  dialogVisible = signal(false);
  editing = signal<Category | null>(null);
  submitting = signal(false);

  contingentLookup = computed(() => {
    const map = new Map<number, string>();
    for (const c of this.contingentService.contingents()) {
      map.set(c.id, c.title);
    }
    return map;
  });

  form: FormGroup = this.#fb.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    description: [null as string | null],
    contingent_id: [null as number | null, [Validators.required]],
  });

  ngOnInit(): void {
    this.categoryService.getCategories();
    this.contingentService.getContingents();
  }

  contingentTitleFor(id: number | null): string {
    if (id == null) return '';
    return this.contingentLookup().get(id) ?? `#${id}`;
  }

  openCreateDialog() {
    this.editing.set(null);
    this.form.reset({ title: '', description: null, contingent_id: null });
    this.dialogVisible.set(true);
  }

  openEditDialog(category: Category) {
    this.editing.set(category);
    this.form.reset({
      title: category.title,
      description: category.description ?? null,
      contingent_id: category.contingent_id ?? null,
    });
    this.dialogVisible.set(true);
  }

  closeDialog() {
    this.dialogVisible.set(false);
    this.editing.set(null);
  }

  onSubmit() {
    if (this.submitting()) return;
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const parsed = StoreCategorySchema.safeParse({
      title: raw.title,
      description: raw.description,
      contingent_id: raw.contingent_id,
    });
    if (!parsed.success) {
      applyZodErrors(parsed.error, this.form);
      return;
    }

    const editing = this.editing();
    this.submitting.set(true);

    const call$ = editing
      ? this.categoryService.updateCategory(editing.id, parsed.data)
      : this.categoryService.createCategory(parsed.data);

    call$.subscribe({
      next: () => {
        this.submitting.set(false);
        this.dialogVisible.set(false);
        this.editing.set(null);
        this.categoryService.getCategories();
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant(
            editing
              ? 'catalog.categories.updated'
              : 'catalog.categories.created',
          ),
        });
      },
      error: (e: unknown) => {
        this.submitting.set(false);
        this.#applyServerErrors(e);
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant(
            editing
              ? 'catalog.categories.updateError'
              : 'catalog.categories.createError',
          ),
        });
      },
    });
  }

  onRowDelete(event: Event, category: Category) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      header: this.#translate.instant('catalog.categories.deleteConfirmHeader'),
      message: this.#translate.instant(
        'catalog.categories.deleteConfirmMessage',
      ),
      acceptLabel: this.#translate.instant('label.delete'),
      rejectLabel: this.#translate.instant('label.cancel'),
      acceptButtonProps: { severity: 'danger' },
      accept: () => this.#performDelete(category.id),
    });
  }

  #performDelete(id: number) {
    this.categoryService.deleteCategory(id).subscribe({
      next: () => {
        this.categoryService.getCategories();
        this.#messageService.add({
          severity: 'success',
          summary: this.#translate.instant('label.successHeader'),
          detail: this.#translate.instant('catalog.categories.deleted'),
        });
      },
      error: () => {
        this.#messageService.add({
          severity: 'error',
          summary: this.#translate.instant('label.errorHeader'),
          detail: this.#translate.instant('catalog.categories.deleteError'),
        });
      },
    });
  }

  #applyServerErrors(error: unknown) {
    const errBag = (error as { error?: { errors?: Record<string, string[]> } })
      ?.error?.errors;
    if (!errBag) return;
    for (const key of Object.keys(errBag)) {
      const control = this.form.get(key);
      if (control) {
        control.setErrors({ server: { message: errBag[key][0] } });
        control.markAsTouched();
      }
    }
  }
}
